import { useEffect, useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PrescriptionRenewalMedicationHistoryFilterSettingModel } from "../Models/PrescriptionRenewalMedicationHistoryFilterSettingModel";

import styles from "./PrescriptionRenewalsFilterSettings.module.scss";

interface PrescriptionRenewalsMedicationHistoryFilterSettingsProps {
    settings: PrescriptionRenewalMedicationHistoryFilterSettingModel;
    onClose: () => void;
    onSaveChanges: (settings: PrescriptionRenewalMedicationHistoryFilterSettingModel) => void;
    onColumnOrderingPopupOpenClick: () => void;
}

function PrescriptionRenewalsMedicationHistoryFilterSettings(props: PrescriptionRenewalsMedicationHistoryFilterSettingsProps): JSX.Element {
    const { getText } = useTranslation();
    const [settings, setSettings] = useState<PrescriptionRenewalMedicationHistoryFilterSettingModel>(props.settings ?? new PrescriptionRenewalMedicationHistoryFilterSettingModel());
    const overlayKey: string = "PrescriptionRenewalFilterSettingPopupOverlay";
    const medicationStatus: string[] = ["ActiveLabel", "CeasedLabel", "Completed2Label", "Expired3Label"];
    const medicationOrigin: string[] = ["MYLE", "Rx"];

    const isDateFilterInvalid: boolean = settings.dateFrom && settings.dateTo && settings.dateFrom > settings.dateTo;

    const toggleSelectAllOrigins = (isAllSelected: boolean): void => {
        setSettings(prevSettings => ({ ...prevSettings, selectedOrigin: isAllSelected ? medicationOrigin : [] }));
    };

    const toggleSelectAllStatus = (isAllSelected: boolean): void => {
        setSettings(prevSettings => ({ ...prevSettings, selectedStatus: isAllSelected ? medicationStatus : [] }));
    };

    const toggleSelectStatus = (isChecked: boolean, status: string): void => {
        setSettings(prevSettings => ({ ...prevSettings, selectedStatus: isChecked ? [...prevSettings.selectedStatus, status] :
            prevSettings.selectedStatus.filter(selectedStatus => selectedStatus != status) })
        );
    };

    const toggleSelectOrigin = (isChecked: boolean, origin: string): void => {
        setSettings(prevSettings => ({ ...prevSettings, selectedOrigin: isChecked ? [...prevSettings.selectedOrigin, origin] :
            prevSettings.selectedOrigin.filter(selectedOrigin => selectedOrigin != origin) })
        );
    };

    const toggleSelectDetails = (isChecked: boolean): void => {
        setSettings(prevSettings => ({ ...prevSettings, isSelectedDetailsCheck: isChecked }));
    };

    useEffect(() => {
        overlayStore.showOverlay(overlayKey, {
            transparent: true,
            onClicked: () => {
                props.onClose();
            }
        });
    }, [settings, props]);

    useEffect(() => () => {
        overlayStore.hideOverlay(overlayKey);
    }, []);

    return (
        <div className={`${styles.filterSettingsContainer} ${styles.medicationHistory}`}>
            <div className={styles.headers}>
                <div className={styles.dateMinWidth}>
                    {getText("DateLabel").toUpperCase()}
                </div>
                <div className={styles.medicationHistoryHeader}>
                    <CheckboxControl
                        label={getText("PrescriptionStatusLabel").toUpperCase()}
                        checked={settings?.selectedStatus.length === medicationStatus.length}
                        onChange={isChecked => toggleSelectAllStatus(isChecked)}
                    />
                </div>
                <div className={styles.medicationHistoryHeader}>
                    <CheckboxControl
                        label={getText("OriginLabel").toUpperCase()}
                        checked={settings?.selectedOrigin?.length === medicationOrigin.length}
                        onChange={isChecked => toggleSelectAllOrigins(isChecked)}
                    />
                </div>
                <div className={styles.medicationHistoryHeader}>
                    <CheckboxControl
                        label={getText("DetailsLabel").toUpperCase()}
                        checked={settings?.isSelectedDetailsCheck}
                        onChange={isChecked => toggleSelectDetails(isChecked)}
                    />
                </div>
            </div>
            <div className={styles.columns}>
                <div className="date">
                    <MultiDateSelector
                        id="PrescriptionRenewalMedictionHistoryDates"
                        from={settings?.dateFrom}
                        to={settings?.dateTo}
                        fromSetTodayLabel={getText("TodayLabel")}
                        toSetTodayLabel={getText("TodayLabel")}
                        fromChange={date => setSettings({ ...settings, dateFrom: date })}
                        toChange={date => setSettings({ ...settings, dateTo: date })}
                    />
                </div>
                <div className={styles.elements}>
                    {medicationStatus.map(status => (
                        <CheckboxControl
                            id={`status_${status}`}
                            label={getText(status)}
                            checked={settings?.selectedStatus.contains(status)}
                            onChange={isChecked => toggleSelectStatus(isChecked, status)}
                            key={`status_${status}`}
                        />
                    ))}
                </div>
                <div className={styles.elements}>
                    {medicationOrigin.map(origin => (
                        <CheckboxControl
                            id={`origin_${origin}`}
                            label={getText(origin + "Label")}
                            checked={settings?.selectedOrigin.contains(origin)}
                            onChange={isChecked => toggleSelectOrigin(isChecked, origin)}
                            key={`origin_${origin}`}
                        />
                    ))}
                </div>
                <div className={styles.elements}>
                    <CheckboxControl
                        label={getText("IncludeInstructionLabel")}
                        checked={settings?.isSelectedDetailsCheck}
                        onChange={isChecked => toggleSelectDetails(isChecked)}
                    />
                </div>
            </div>
            <div className={styles.footer}>
                <div className="pull-left">
                    <SettingsButton
                        alternativeIcon={true}
                        rightMargin={5}
                        onClick={() => props.onColumnOrderingPopupOpenClick()}
                    />
                </div>
                <div className="pull-right flex-display flex-end flex-gap-xs">
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => props.onClose()}
                        label={cacheStore.GetString("CancelLabel")}
                    />
                    <ButtonControl
                        className="btn btn-primary"
                        disabled={isDateFilterInvalid}
                        onClick={() => props.onSaveChanges(settings)}
                        label={cacheStore.GetString("Ok_Label")}
                    />
                </div>
            </div>
        </div>
    );
}

export { PrescriptionRenewalsMedicationHistoryFilterSettings };
